'use client';

/**
 * VendorCard — a vendor tile for the directory grid.
 *
 * Top-left aligned: the BFF-derived monogram chip, the vendor name (bold), and
 * the product count (muted). Everything it paints — initials, chip colours,
 * count — is shipped by the BFF; the card derives nothing. One tile, reused
 * wherever a vendor is browsed; grid density is the caller's grid.
 *
 * @layer presentation
 * @arch_component_type primitive
 * @arch_domain procurement
 */

import { cn } from '@/lib/utils/cn';
import { VendorMonogram, type VendorLogo } from './vendor-monogram';

export interface VendorCardProps {
  name: string;
  monogram: string;
  monogramBg?: string;
  monogramFg?: string;
  productCount?: number | null;
  logo?: VendorLogo | null;
  onClick?: () => void;
  className?: string;
}

export function VendorCard({
  name,
  monogram,
  monogramBg,
  monogramFg,
  logo,
  onClick,
  className,
}: VendorCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={name}
      className={cn(
        'group flex w-full flex-col items-start rounded-xl border bg-card p-5 text-left',
        'hover:border-primary/30 hover:shadow-sm transition-all',
        className,
      )}
    >
      {/* Size-gate: a real logo earns a wide lockup band (wordmarks read);
          the monogram floor keeps the square chip. */}
      <VendorMonogram
        name={name}
        initials={monogram}
        bg={monogramBg}
        fg={monogramFg}
        logo={logo}
        className={logo ? 'mb-3 h-14 w-full' : 'mb-3 h-11 w-11 text-sm'}
      />
      <div className="min-w-0 w-full">
        <div className="line-clamp-2 text-[15px] font-semibold leading-snug text-foreground">
          {name}
        </div>
      </div>
    </button>
  );
}

/**
 * VendorGrid — product-density grid of vendor tiles (≈15–20 on a large screen).
 * Every vendor carries its BFF-derived mark (`monogram`, `monogram_bg/_fg`).
 */
export function VendorGrid({
  vendors,
  onSelect,
}: {
  vendors: Array<{
    semantic_id: string;
    name: string;
    monogram: string;
    monogram_bg?: string;
    monogram_fg?: string;
    product_count?: number | null;
    logo?: VendorLogo | null;
  }>;
  onSelect?: (semanticId: string) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
      {vendors.map((v) => (
        <VendorCard
          key={v.semantic_id}
          name={v.name}
          monogram={v.monogram}
          monogramBg={v.monogram_bg}
          monogramFg={v.monogram_fg}
          productCount={v.product_count}
          logo={v.logo}
          onClick={onSelect ? () => onSelect(v.semantic_id) : undefined}
        />
      ))}
    </div>
  );
}
